import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';

import {
  MapPin,
} from 'lucide-react';

import {
  cities,
  type City,
} from '../../../utils/cities';

interface FormProps {
  formData: any;
  onChange: (
    id: string,
    value: string
  ) => void;
}

export const AstrologyIntake: React.FC<FormProps> = ({
  formData,
  onChange,
}) => {
  const [
    query,
    setQuery,
  ] = useState('');

  const [
    isOpen,
    setIsOpen,
  ] = useState(false);

  const dropdownRef =
    useRef<HTMLDivElement>(null);


  // =========================================================
  // GET SAVED CITY FROM PARENT FORM DATA
  // =========================================================

  const savedCity = useMemo(() => {
    if (!formData?.pob) {
      return '';
    }

    try {
      const parsed =
        typeof formData.pob === 'string'
          ? JSON.parse(formData.pob)
          : formData.pob;

      if (parsed?.city) {
        return parsed.state
          ? `${parsed.city}, ${parsed.state}`
          : parsed.city;
      }

      return '';
    } catch {
      return typeof formData.pob === 'string'
        ? formData.pob
        : '';
    }
  }, [
    formData?.pob,
  ]);


  // =========================================================
  // DISPLAY VALUE
  //
  // If user has started typing, use query.
  // Otherwise show the saved city from formData.
  // =========================================================

  const displayedQuery =
    query || savedCity;


  // =========================================================
  // CITY RESULTS
  //
  // Derived instead of setState inside useEffect.
  // This removes the React cascading-render warning.
  // =========================================================

  const results = useMemo(() => {
    if (displayedQuery.trim().length < 2) {
      return [];
    }

    const filtered = cities.filter((city) =>
      city.city
        .toLowerCase()
        .includes(
          displayedQuery.toLowerCase()
        ) ||
      city.state
        .toLowerCase()
        .includes(
          displayedQuery.toLowerCase()
        )
    );

    return Array.from(
      new Map(
        filtered.map(
          item => [
            item.city,
            item,
          ]
        )
      ).values()
    ).slice(0, 8);

  }, [
    displayedQuery,
  ]);


  // =========================================================
  // CLICK OUTSIDE
  // =========================================================

  useEffect(() => {

    const handleClickOutside = (
      event: MouseEvent
    ) => {

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(
          event.target as Node
        )
      ) {
        setIsOpen(false);
      }

    };

    document.addEventListener(
      'mousedown',
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );
    };

  }, []);


  // =========================================================
  // SELECT CITY
  // =========================================================

  const handleSelectCity = (
    selectedCity: City
  ) => {

    const locationData = {
      city: selectedCity.city,
      state: selectedCity.state,
      lat: selectedCity.lat,
      lng: selectedCity.lng,
    };

    setQuery(
      `${selectedCity.city}, ${selectedCity.state}`
    );

    setIsOpen(false);

    onChange(
      'pob',
      JSON.stringify(locationData)
    );
  };


  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

      {/* =====================================================
          TIME OF BIRTH
      ===================================================== */}

      <div className="flex flex-col gap-1.5">

        <label className="text-sm font-medium text-gray-700">
          Time of Birth
        </label>

        <input
          type="time"
          value={
            formData.tob || ''
          }
          onChange={(e) =>
            onChange(
              'tob',
              e.target.value
            )
          }
          className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
        />

      </div>


      {/* =====================================================
          PLACE OF BIRTH
      ===================================================== */}

      <div
        className="flex flex-col gap-1.5 relative"
        ref={dropdownRef}
      >

        <label className="text-sm font-medium text-gray-700">
          Place of Birth
        </label>

        <div className="relative">

          <input
            type="text"
            placeholder="Search city..."
            value={displayedQuery}
            onChange={(e) => {

              const value =
                e.target.value;

              setQuery(value);

              setIsOpen(true);

              // User is changing the saved city.
              // Clear the previously selected coordinates.
              if (formData.pob) {
                onChange(
                  'pob',
                  ''
                );
              }

            }}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
          />

        </div>


        {/* =================================================
            CITY DROPDOWN
        ================================================= */}

        {isOpen &&
          results.length > 0 && (

            <ul className="absolute top-[100%] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto divide-y divide-gray-50">

              {results.map(
                (
                  place,
                  idx
                ) => (

                  <li
                    key={idx}
                    onClick={() =>
                      handleSelectCity(
                        place
                      )
                    }
                    className="px-3 py-2.5 hover:bg-blue-50 cursor-pointer flex items-center gap-2 transition-colors"
                  >

                    <MapPin className="w-4 h-4 text-gray-400 shrink-0" />

                    <div className="flex flex-col">

                      <span className="text-sm font-medium text-gray-900">
                        {place.city}
                      </span>

                      <span className="text-[11px] text-gray-500">
                        {place.state}, India
                      </span>

                    </div>

                  </li>

                )
              )}

            </ul>

          )}

      </div>

    </div>
  );
};